import { isTizen } from "@blacknut/javascript-sdk/dist";
import {
  Focusable,
  JsSpatialNavigation,
  useSpatialNavigation,
} from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import * as React from "react";
import { ModalSubscription, useModal } from "../../utils";
import VirtualKeyboardModal from "../Modals/VirtualKeyboardModal";
import styles from "./styles.module.scss";

export interface TextInputProps {
  error?: string | boolean;
  startAdornment?: React.ReactElement<unknown>;
  endAdornment?: React.ReactElement<unknown>;
  rows?: number;
  focusKey?: string;
  label?: string;
  testID?: string;
  id?: string;
  wrapClassName?: string;
  //   textInputStyle?: Partial<TextInputStyle>;
  value?: string;
  disabled?: boolean;
  className?: string;
  type?: "email" | "text" | "password" | "number";
  name?: string;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  inputProps?: Partial<
    React.HTMLAttributes<HTMLInputElement | HTMLTextAreaElement>
  >;
  placeholder?: string;
  nextFocusForward?: { DOWN?: string };
  icon?: JSX.Element;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;

  useVirtualKeyboard?: boolean;
}

const Input = ({
  rows,
  id,
  testID,
  startAdornment,
  endAdornment,
  onInputFocused,
  onInputBlur,
  type = "text",
  name,
  onChange,
  inputProps,
  placeholder,
  value,
  disabled,
  inputRef,
  onKeyDown,
  onKeyUp,
  error,
  //   textInputStyle,
}: TextInputProps & {
  onInputFocused: () => void;
  onInputBlur: () => void;
  inputRef: React.MutableRefObject<HTMLElement | null>;
  error?: string | boolean;
}) => {
  const remainingProps = { ...inputProps };
  delete remainingProps.children;
  const input = React.createElement(rows === undefined ? "input" : "textarea", {
    ...remainingProps,
    onBlur: onInputBlur,
    onFocus: onInputFocused,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    ref: inputRef,
    type,
    id,
    name,
    onChange,
    value,
    placeholder,
    rows,
    disabled,
    "data-testid": testID,
    className: clsx([!!error && styles.errored]),
  });

  return (
    <>
      {startAdornment}
      {input}
      {endAdornment}
    </>
  );
};
const TextInput = (props: React.PropsWithChildren<TextInputProps>) => {
  const { active } = useSpatialNavigation();

  const {
    label,
    value,
    className,
    id,
    error,
    wrapClassName,
    disabled,
    // textInputStyle,
    children,
    nextFocusForward,
    icon,
    onKeyUp,
    onKeyDown,
    useVirtualKeyboard = false,
    onChange,
  } = props;
  let filled = false;
  if (typeof value === "string") {
    filled = (value as string).trim().length > 0;
  } else {
    filled = value !== undefined;
  }

  const [inputFocused, setInputFocused] = React.useState(false);

  const { active: spatialNavigationActive } = useSpatialNavigation();
  const ref = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const onInputBlur = React.useCallback(() => {
    setInputFocused(false);
    if (spatialNavigationActive) {
      const focusable = ref.current && ref.current.closest("[tabIndex]");
      if (focusable) {
        (focusable as HTMLElement).focus();
      }
    }
  }, [spatialNavigationActive]);
  const onInputFocus = React.useCallback(() => {
    setInputFocused(true);
  }, []);

  const { push } = useModal();

  const onChangeVK = React.useCallback(
    (e: string) => {
      onChange?.({
        target: { value: e },
      } as React.ChangeEvent<HTMLInputElement>);
    },
    [onChange]
  );
  const modalSubscription = React.useRef<ModalSubscription>();

  const closeKeyboard = React.useCallback(() => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;
  }, []);
  const onPressEnter = React.useCallback(() => {
    if (useVirtualKeyboard) {
      modalSubscription.current = push((props) => {
        return (
          <VirtualKeyboardModal
            {...props}
            onClose={closeKeyboard}
            value={value}
            setValue={onChangeVK}
          ></VirtualKeyboardModal>
        );
      });
    } else if (!inputFocused && active) {
      setInputFocused(true);
      ref.current?.focus();
    }
  }, [active, inputFocused, useVirtualKeyboard]);

  const _onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isTizen()) {
        if (e.keyCode === 65376 || e.keyCode === 65385) {
          // FIXME check if still the case when in a iframe
          // when you press the Done/Cancel button in OSK
          // https://blacknut.atlassian.net/browse/CA-297

          if (e.keyCode === 65376 && nextFocusForward && ref.current) {
            e.currentTarget.blur();
            e.stopPropagation();

            setTimeout(() => {
              JsSpatialNavigation.move(nextFocusForward, ref.current, {
                focusText: true,
              });
            }, 200);
          } else {
            // Unfocus current element
            e.currentTarget.blur();
            e.stopPropagation();
            setTimeout(() => {
              const focusable =
                ref.current && ref.current.closest("[tabIndex]");
              if (focusable) {
                (focusable as HTMLElement).focus();
              }
            }, 200);
          }
        }
      } else {
        if (e.key === "Enter") {
          // Unfocus current element
          e.currentTarget.blur();
          e.stopPropagation();
          setTimeout(() => {
            const focusable = ref.current && ref.current.closest("[tabIndex]");
            if (focusable) {
              (focusable as HTMLElement).focus();
            }
          }, 200);
        }
        if (onKeyDown) {
          onKeyDown(e);
        }
      }
    },
    [nextFocusForward, onKeyDown]
  );

  return (
    <Focusable
      className={clsx([styles.container, className])}
      role="input-wrapper"
      onPressEnter={onPressEnter}
    >
      {label && (
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
      )}
      <div
        className={clsx([
          styles.wrap,
          !!error && styles.errored,
          wrapClassName,
        ])}
      >
        {icon && icon}
        {children && <>{children}</>}
        {!children && (
          <Input
            {...props}
            inputRef={ref}
            onInputBlur={onInputBlur}
            onInputFocused={onInputFocus}
            onKeyDown={_onKeyDown}
            onKeyUp={onKeyUp}
            error={error}
          />
        )}
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </Focusable>
  );
};
export default TextInput;
export { TextInput };
